import React, { useEffect, useState } from 'react';
import { fetchLevelProbabilities, type LevelProbData } from './api';

interface Props {
  instrument: string;
  snapshot: {
    prev_high: number;
    prev_low: number;
    yesterday_high: number;
    yesterday_low: number;
  } | null;
}

const LEVEL_LABELS: Record<string, string> = {
  prev_sess_high: 'Prev Session High',
  prev_sess_low: 'Prev Session Low',
  prev_daily_high: 'Prev Daily High',
  prev_daily_low: 'Prev Daily Low',
  prev_week_high: 'Prev Week High',
  prev_week_low: 'Prev Week Low',
};

const LEVEL_KEYS = ['prev_sess_high', 'prev_sess_low', 'prev_daily_high', 'prev_daily_low', 'prev_week_high', 'prev_week_low'];

function levelColor(pct: number) {
  return pct >= 70 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
}

export default function LevelProbabilitiesPanel({ instrument, snapshot }: Props) {
  const [data, setData] = useState<LevelProbData>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchLevelProbabilities(instrument).then(d => {
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [instrument]);

  if (loading) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>Level Probabilities</div>
        <div style={styles.grid}>
          {LEVEL_KEYS.map(lk => (
            <div key={lk} style={styles.card}>
              <div style={{ ...styles.skelLine, width: '80%', height: 14 }} />
              <div style={{ ...styles.skelLine, width: '50%', height: 20, marginTop: 8 }} />
              <div style={{ ...styles.skelLine, width: '60%', height: 10, marginTop: 6 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasData = Object.keys(data).length > 0;
  if (!hasData) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>Level Probabilities</div>
        <div style={styles.empty}>Not enough data for level probabilities</div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>Level Probabilities</div>
      <div style={styles.grid}>
        {LEVEL_KEYS.map(lk => {
          const d = data[lk];
          if (!d) return null;
          const color = levelColor(d.hitRate);
          // Price: estimate from snapshot or use placeholder
          const price = snapshot
            ? lk === 'prev_sess_high' ? snapshot.prev_high
            : lk === 'prev_sess_low' ? snapshot.prev_low
            : lk === 'prev_daily_high' ? snapshot.yesterday_high
            : lk === 'prev_daily_low' ? snapshot.yesterday_low
            : lk === 'prev_week_high' ? snapshot.yesterday_high
            : lk === 'prev_week_low' ? snapshot.yesterday_low
            : null
            : null;

          return (
            <div key={lk} style={styles.card}>
              <div style={styles.levelName}>{LEVEL_LABELS[lk]}</div>
              {price != null && (
                <div style={styles.levelPrice}>{price.toFixed(2)}</div>
              )}
              <div style={styles.chip} data-color={color}>
                <span style={{ ...styles.chipPct, color }}>{d.hitRate.toFixed(1)}%</span>
              </div>
              {d.avgCandleHit > 0 && (
                <div style={styles.avgCandle}>Avg: Candle {d.avgCandleHit}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background: 'rgba(22,24,42,0.85)',
    borderRadius: 14,
    padding: '18px 22px',
    borderTop: '3px solid #8b5cf6',
    backdropFilter: 'blur(20px)',
  },
  header: {
    fontSize: 13,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 12,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  card: {
    background: '#334155',
    borderRadius: 8,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  levelName: {
    fontSize: 11,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  levelPrice: {
    fontSize: 18,
    fontWeight: 700,
    color: '#f8fafc',
    fontFamily: 'monospace',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  chipPct: {
    fontSize: 16,
    fontWeight: 800,
  },
  sample: {
    fontSize: 11,
    color: '#475569',
  },
  avgCandle: {
    fontSize: 10,
    color: '#64748b',
  },
  skeleton: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  skelLine: {
    background: '#334155',
    borderRadius: 8,
  },
  empty: {
    color: '#475569',
    fontSize: 13,
    textAlign: 'center',
    padding: '16px 0',
  },
};